import { Link, useLocation, useNavigate } from "react-router-dom";
import { Cpu, Sun, Moon, LogOut, Zap, Shield, UserCog } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const BASE_LINKS = [
  { to: "/", label: "Diagnostic" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/tendances", label: "À la une" },
  { to: "/comparateur", label: "Comparateur" },
  { to: "/profils", label: "Profils" },
  { to: "/favoris", label: "Favoris" },
  { to: "/assistant", label: "Assistant IA" },
  { to: "/installation", label: "Installer" },
  { to: "/iphone", label: "iPhone / iPad" },
  { to: "/a-propos", label: "À propos" },
];

export function Navbar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const LINKS = user && user.role === "admin" ? [...BASE_LINKS, { to: "/admin", label: "Admin" }] : BASE_LINKS;
  const [dark, setDark] = useState(() => localStorage.getItem("llmready_theme") !== "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("llmready_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#1E2638] bg-[#080B10]/80 backdrop-blur-xl dark:bg-[#080B10]/80">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
        <Link to="/" className="mr-4 flex shrink-0 items-center gap-2" data-testid="nav-logo">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Cpu className="h-5 w-5" />
          </span>
          <span className="font-head text-lg font-extrabold tracking-tight">
            llm<span className="text-primary">etric</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex">
          {LINKS.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
                  active ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setDark((d) => !d)}
            data-testid="theme-toggle"
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Basculer le thème"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav("/compte")} data-testid="account-link" className="gap-1.5" title={user.name}>
                <UserCog className="h-4 w-4 shrink-0" /> <span className="hidden max-w-[7rem] truncate sm:inline 2xl:max-w-[12rem]">{user.name}</span>
              </Button>
              <button onClick={logout} data-testid="logout-button" aria-label="Déconnexion" className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Button size="sm" onClick={() => nav("/login")} data-testid="login-cta" className="gap-1.5">
              <Zap className="h-4 w-4" /> Connexion
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-t border-[#1E2638] px-3 py-2 xl:hidden">
        {LINKS.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${
              loc.pathname === l.to ? "bg-accent text-foreground" : "text-muted-foreground"
            }`}
          >
            {l.label}
          </Link>
        ))}
      </div>
    </header>
  );
}
